import { Eye, Pencil, RefreshCcw, Trash2, ShieldAlert, ShieldCheck } from 'lucide-react'
import { getProfileMeta, getStatusMeta } from '@services/userManagementService'
import { formatDateTime, formatRelativeAccess, getInitials, getPermissionLabel } from './usersUtils'
import OverflowAwareContainer from '@components/shared/OverflowAwareContainer'

function UserBadge({ className, children }) {
  return <span className={`users-badge ${className}`}>{children}</span>
}

export default function UserTable({ users, loading, pagination, onPageChange, onPageSizeChange, onAction, canEdit = false }) {
  if (loading) {
    return (
      <div className="users-table-card">
        <div className="users-table-skeleton">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="users-skeleton-row" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <section className="users-table-card">
      <OverflowAwareContainer className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Perfil</th>
              <th>Permissões</th>
              <th>Último acesso</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const profileMeta = getProfileMeta(user.profile)
              const statusMeta = getStatusMeta(user.status)

              return (
                <tr key={user.id} className={user.status !== 'ACTIVE' ? 'is-muted' : ''}>
                  <td>
                    <div className="users-name-cell">
                      <div className="users-avatar">{getInitials(user.fullName)}</div>
                      <div>
                        <strong>{user.fullName}</strong>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <UserBadge className={profileMeta?.badgeClass}>{profileMeta?.label}</UserBadge>
                  </td>
                  <td>
                    <span className="users-permissions-summary" title={user.permissions.map(getPermissionLabel).join(', ')}>
                      {user.permissions.slice(0, 2).map(getPermissionLabel).join(' • ')}
                      {user.permissions.length > 2 ? ' +' : ''}
                    </span>
                  </td>
                  <td>
                    <div className="users-date-stack">
                      <strong>{formatRelativeAccess(user.lastAccessAt)}</strong>
                      <span>{formatDateTime(user.lastAccessAt)}</span>
                    </div>
                  </td>
                  <td>
                    <UserBadge className={statusMeta?.badgeClass}>{statusMeta?.label}</UserBadge>
                  </td>
                  <td>
                    <div className="users-actions-group">
                      <button type="button" className="users-icon-btn" title="Visualizar" onClick={() => onAction('view', user)}>
                        <Eye size={16} />
                      </button>
                      {canEdit ? <>
                        <button type="button" className="users-icon-btn" title="Editar" onClick={() => onAction('edit', user)}>
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="users-icon-btn"
                          title={user.status === 'ACTIVE' ? 'Inativar usuário' : 'Ativar usuário'}
                          onClick={() => onAction('toggleStatus', user)}
                        >
                          {user.status === 'ACTIVE' ? <ShieldAlert size={16} /> : <ShieldCheck size={16} />}
                        </button>
                        <button type="button" className="users-icon-btn" title="Resetar senha" onClick={() => onAction('resetPassword', user)}>
                          <RefreshCcw size={16} />
                        </button>
                        <button type="button" className="users-icon-btn danger" title="Excluir usuário" onClick={() => onAction('delete', user)}>
                          <Trash2 size={16} />
                        </button>
                      </> : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </OverflowAwareContainer>

      <footer className="users-pagination">
        <div>
          Exibindo <strong>{users.length}</strong> registros nesta página • Total <strong>{pagination.total}</strong>
        </div>
        <div className="users-pagination-controls">
          <select value={pagination.pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))} className="users-select users-select-sm">
            {[5, 10, 20].map((size) => <option key={size} value={size}>{size} / página</option>)}
          </select>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => onPageChange(pagination.page - 1)} disabled={pagination.page <= 1}>
            Anterior
          </button>
          <span>Página {pagination.page} de {pagination.totalPages}</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => onPageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}>
            Próxima
          </button>
        </div>
      </footer>
    </section>
  )
}
